import { pb } from "./pocketbase";
import { logAction } from "./logService";

export const conciliacaoService = {
  // ─── 1. PARSER OFX (SGML / XML RESILIENTE VIA REGEX) ───────────────────
  parseOFX(text) {
    const transactions = [];
    // Encontra todos os blocos <STMTTRN>...</STMTTRN>
    const matches = text.match(/<STMTTRN>([\s\S]*?)(?:<\/STMTTRN>|$)/gi) || [];

    matches.forEach((block) => {
      // Tags OFX podem ou não ter fechamento </TAG>. Lemos tudo até o próximo < ou quebra de linha.
      const fitid = (block.match(/<FITID>([^\r\n<]+)/i) || [])[1]?.trim();
      const trnamt = (block.match(/<TRNAMT>([^\r\n<]+)/i) || [])[1]?.trim();
      const dtposted = (block.match(/<DTPOSTED>([^\r\n<]+)/i) || [])[1]?.trim();
      
      // Memo ou Name servem como a descrição da transação do banco
      const memo = (
        block.match(/<MEMO>([^\r\n<]+)/i) || 
        block.match(/<NAME>([^\r\n<]+)/i) || 
        []
      )[1]?.trim();

      if (fitid && trnamt && dtposted) {
        // Converte valor float string para centavos inteiros (Ex: -150.00 -> -15000)
        const valFloat = parseFloat(trnamt);
        const valCents = Math.round(valFloat * 100);

        // Formata data de YYYYMMDD... para YYYY-MM-DD
        const year = dtposted.slice(0, 4);
        const month = dtposted.slice(4, 6);
        const day = dtposted.slice(6, 8);
        const dateStr = `${year}-${month}-${day}`;

        transactions.push({
          fitid,
          valor_centavos: valCents,
          data: dateStr,
          descricao: memo || "Transação Sem Descrição"
        });
      }
    });

    const bankName = (text.match(/<ORG>([^\r\n<]+)/i) || [])[1]?.trim() || "Extrato Bancário";
    const acctId = (text.match(/<ACCTID>([^\r\n<]+)/i) || [])[1]?.trim() || "";

    return {
      bankName,
      acctId,
      transactions
    };
  },

  // ─── 2. IMPORTADOR DE EXTRATOS COM IDEMPOTÊNCIA ───────────────────────
  async importExtrato(contaBancariaId, filename, transactions, currentUser) {
    if (!transactions || transactions.length === 0) {
      throw new Error("Nenhuma transação encontrada no extrato para importar.");
    }

    // Busca fitids já existentes em lotes de 30 para evitar filtros gigantescos no banco
    const fitids = transactions.map(t => t.fitid);
    const existingFitids = new Set();
    
    for (let i = 0; i < fitids.length; i += 30) {
      const chunk = fitids.slice(i, i + 30);
      const filter = chunk.map(id => `fitid = "${id}"`).join(" || ");
      try {
        const found = await pb.collection("fin_extrato_transacoes").getFullList({ filter });
        found.forEach(item => existingFitids.add(item.fitid));
      } catch (err) {
        console.warn("[Conciliacao] Erro ao pesquisar fitids existentes:", err.message);
      }
    }

    // Filtra apenas as novas transações
    const newTransactions = transactions.filter(t => !existingFitids.has(t.fitid));

    if (newTransactions.length === 0) {
      return {
        loteId: null,
        total: transactions.length,
        newCount: 0,
        skippedCount: transactions.length
      };
    }

    // Identifica período de início e fim
    const sortedDates = [...transactions].map(t => t.data).sort();
    const periodStart = sortedDates[0];
    const periodEnd = sortedDates[sortedDates.length - 1];

    // 1. Cria o Lote de Extrato
    const lote = await pb.collection("fin_extratos_importados").create({
      conta_bancaria: contaBancariaId,
      formato: "ofx",
      nome_arquivo: filename,
      periodo_inicio: periodStart,
      periodo_fim: periodEnd,
      importado_por: currentUser.id,
      total_transacoes: transactions.length,
      total_conciliadas: 0,
      total_ignoradas: 0,
      status: "em_conciliacao"
    });

    // 2. Cria as transações individuais não importadas ainda
    for (const t of newTransactions) {
      await pb.collection("fin_extrato_transacoes").create({
        lote: lote.id,
        conta_bancaria: lote.conta_bancaria,
        data: t.data,
        valor_centavos: t.valor_centavos,
        descricao: t.descricao,
        fitid: t.fitid,
        status: "nao_conciliado"
      });
    }

    await logAction(currentUser, "FINANCEIRO", "EXTRATO_IMPORTADO", `Importou extrato ${filename} com ${newTransactions.length} novas transações.`, {
      loteId: lote.id,
      contaBancariaId,
      total: transactions.length,
      newCount: newTransactions.length
    });

    return {
      loteId: lote.id,
      total: transactions.length,
      newCount: newTransactions.length,
      skippedCount: transactions.length - newTransactions.length
    };
  },

  // ─── 3. MOTOR DE MATCHING DE TRANSAÇÕES ────────────────────────────────
  async getMatchesForTransaction(transaction) {
    // Definimos limite de +/- 3 dias conforme plano mestre
    const txDate = new Date(transaction.data + "T12:00:00Z");
    const dateMin = new Date(txDate.getTime() - 3 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];
    const dateMax = new Date(txDate.getTime() + 3 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];

    // Busca candidatos no ledger fin_lancamentos:
    // - Mesma conta bancária
    // - Lançamento deve estar "pago"
    // - Sem transacao_bancaria vinculada
    // - Valor exato (incluindo sinal)
    const filter = `valor_centavos = ${transaction.valor_centavos} && data >= "${dateMin}" && data <= "${dateMax}" && transacao_bancaria = null && conta_bancaria = "${transaction.conta_bancaria}"`;

    try {
      return await pb.collection("fin_lancamentos").getFullList({
        filter,
        sort: "data",
        expand: "categoria,centro_custo"
      });
    } catch (err) {
      console.warn("[Conciliacao] Erro ao buscar candidatos para match:", err.message);
      return [];
    }
  },

  // ─── 4. VINCULAR MATCH DIRECTAMENTE NO LEDGER ────────────────────────
  async vincularTransacao(transacaoId, lancamentoId, currentUser) {
    const transacao = await pb.collection("fin_extrato_transacoes").getOne(transacaoId);
    if (transacao.status !== "nao_conciliado") {
      throw new Error("Esta transação já foi conciliada ou ignorada.");
    }

    const lancamento = await pb.collection("fin_lancamentos").getOne(lancamentoId);
    if (lancamento.transacao_bancaria) {
      throw new Error("Este lançamento já possui vínculo de conciliação.");
    }

    // 1. Vincula a transação no Lançamento Ledger
    await pb.collection("fin_lancamentos").update(lancamentoId, {
      transacao_bancaria: transacaoId,
      valor_conciliado_centavos: transacao.valor_centavos
    });

    // 2. Atualiza a transação do extrato
    await pb.collection("fin_extrato_transacoes").update(transacaoId, {
      status: "conciliado",
      lancamento_vinculado: lancamentoId,
      conciliado_por: currentUser.id,
      data_conciliacao: new Date().toISOString()
    });

    // 3. Atualiza os contadores do lote
    if (transacao.lote) {
      try {
        const lote = await pb.collection("fin_extratos_importados").getOne(transacao.lote);
        await pb.collection("fin_extratos_importados").update(transacao.lote, {
          total_conciliadas: (lote.total_conciliadas || 0) + 1
        });
      } catch (e) {
        console.warn("Erro ao atualizar contadores de conciliação do lote:", e.message);
      }
    }

    await logAction(currentUser, "FINANCEIRO", "CONCILIACAO_VINCULADA", `Conciliou transação ${transacao.descricao} com lançamento ${lancamento.descricao}`, {
      transacaoId,
      lancamentoId
    });

    return true;
  },

  // ─── 5. DESCONSIDERAR OU IGNORAR UMA TRANSAÇÃO ───────────────────────
  async ignorarTransacao(transacaoId, motivo, currentUser) {
    const transacao = await pb.collection("fin_extrato_transacoes").getOne(transacaoId);
    if (transacao.status !== "nao_conciliado") {
      throw new Error("Esta transação já foi resolvida.");
    }

    await pb.collection("fin_extrato_transacoes").update(transacaoId, {
      status: "ignorado",
      motivo_ignorado: motivo,
      conciliado_por: currentUser.id,
      data_conciliacao: new Date().toISOString()
    });

    // Atualiza contadores do lote
    if (transacao.lote) {
      try {
        const lote = await pb.collection("fin_extratos_importados").getOne(transacao.lote);
        await pb.collection("fin_extratos_importados").update(transacao.lote, {
          total_ignoradas: (lote.total_ignoradas || 0) + 1
        });
      } catch (e) {
        console.warn("Erro ao atualizar contadores de ignore do lote:", e.message);
      }
    }

    await logAction(currentUser, "FINANCEIRO", "CONCILIACAO_IGNORADA", `Marcou transação ${transacao.descricao} como ignorada. Motivo: ${motivo}`, {
      transacaoId,
      motivo
    });

    return true;
  },

  // ─── 6. CRIAR PENDÊNCIA (ENVIAR PARA A FILA DE STAGING) ─────────────────
  async criarPendenciaDaTransacao(transacaoId, categoriaId, centroId, currentUser) {
    const trans = await pb.collection("fin_extrato_transacoes").getOne(transacaoId);
    if (trans.status !== "nao_conciliado") {
      throw new Error("Esta transação já foi resolvida.");
    }

    // Cria pendência no staging queue (Invariante I3)
    const isDespesa = trans.valor_centavos < 0;
    
    await pb.collection("fin_lancamentos_pendentes").create({
      origem: "conciliacao_bancaria",
      origem_id: transacaoId,
      tipo_pendencia: isDespesa ? "estorno" : "entrada",
      snapshot: trans,
      categoria_sugerida: categoriaId,
      centro_custo_sugerido: centroId,
      valor_sugerido_centavos: trans.valor_centavos,
      data_sugerida: trans.data,
      descricao_sugerida: `[Banco] ${trans.descricao}`,
      status: "pendente"
    });

    // Atualiza a transação do extrato bancário
    await pb.collection("fin_extrato_transacoes").update(transacaoId, {
      status: "conciliado", // Fica marcado como conciliado no extrato por ter gerado uma pendência no ledger
      conciliado_por: currentUser.id,
      data_conciliacao: new Date().toISOString()
    });

    // Atualiza os contadores do lote
    if (trans.lote) {
      try {
        const lote = await pb.collection("fin_extratos_importados").getOne(trans.lote);
        await pb.collection("fin_extratos_importados").update(trans.lote, {
          total_conciliadas: (lote.total_conciliadas || 0) + 1
        });
      } catch (e) {
        console.warn("Erro ao atualizar contadores do lote:", e.message);
      }
    }

    await logAction(currentUser, "FINANCEIRO", "CONCILIACAO_PENDENCIA_GERADA", `Gerou pendência staging para transação do banco: ${trans.descricao}`, {
      transacaoId,
      categoriaId,
      centroId
    });

    return true;
  },

  async conciliarGrupo(transacaoIds, lancamentoIds, valorTaxaCentavos, currentUser) {
    if (!transacaoIds || transacaoIds.length === 0) {
      throw new Error("Selecione pelo menos uma transação bancária.");
    }
    if (!lancamentoIds || lancamentoIds.length === 0) {
      throw new Error("Selecione pelo menos um lançamento do Ledger.");
    }

    const transacoes = await Promise.all(transacaoIds.map(id => pb.collection("fin_extrato_transacoes").getOne(id)));
    const lancamentos = await Promise.all(lancamentoIds.map(id => pb.collection("fin_lancamentos").getOne(id)));

    if (transacoes.some(t => t.status !== "nao_conciliado")) {
      throw new Error("Uma ou mais transações selecionadas já foram conciliadas.");
    }
    if (lancamentos.some(l => l.transacao_bancaria)) {
      throw new Error("Um ou mais lançamentos selecionados já possuem conciliação.");
    }

    let somaTransacoes = 0;
    transacoes.forEach(t => {
      somaTransacoes += (t.valor_centavos || 0);
    });

    let somalancamentos = 0;
    lancamentos.forEach(l => {
      somalancamentos += (l.valor_centavos || 0);
    });

    const diff = somalancamentos - somaTransacoes;
    if (diff !== valorTaxaCentavos) {
      throw new Error(`Inconsistência de valores: Soma Lançamentos (${somalancamentos/100}) - Soma Transações (${somaTransacoes/100}) = ${diff/100}, mas taxa informada foi ${valorTaxaCentavos/100}.`);
    }

    let lancamentoTaxaId = null;

    if (valorTaxaCentavos > 0) {
      let categoriaId = null;
      try {
        const cats = await pb.collection("fin_categorias").getFullList();
        const found = cats.find(c => c.nome.includes("TAXAS DE CARTÃO") || c.nome.includes("TARIFAS BANCÁRIAS")) ||
                      cats.find(c => c.nome.includes("TAXA") || c.nome.includes("TARIFA"));
        if (found) categoriaId = found.id;
      } catch (e) {
        console.warn("Erro ao buscar categoria de taxas:", e.message);
      }

      const txExemplo = transacoes[0];
      const lcExemplo = lancamentos[0];

      const taxaPayload = {
        data: txExemplo.data,
        descricao: `TAXA DE CARTÃO - Conciliação Grupo [${txExemplo.descricao}]`,
        valor_centavos: -Math.abs(valorTaxaCentavos),
        categoria: categoriaId || lcExemplo.categoria,
        centro_custo: lcExemplo.centro_custo,
        conta_bancaria: txExemplo.conta_bancaria,
        status: "pago",
        tipo_lancamento: "normal",
        observacoes: `Lançamento automático de taxa para conciliação do grupo transações: ${transacaoIds.join(", ")}`
      };

      const taxaLanc = await pb.collection("fin_lancamentos").create(taxaPayload);
      lancamentoTaxaId = taxaLanc.id;
    }

    const grupo = await pb.collection("fin_conciliacao_grupos").create({
      transacoes: transacaoIds,
      lancamentos: lancamentoIds,
      valor_taxa_centavos: valorTaxaCentavos,
      lancamento_taxa: lancamentoTaxaId,
      conciliado_por: currentUser.id,
      data: new Date().toISOString().split("T")[0]
    });

    for (const t of transacoes) {
      await pb.collection("fin_extrato_transacoes").update(t.id, {
        status: "conciliado",
        lancamento_vinculado: lancamentos[0].id,
        conciliado_por: currentUser.id,
        data_conciliacao: new Date().toISOString()
      });
      
      if (t.lote) {
        try {
          const lote = await pb.collection("fin_extratos_importados").getOne(t.lote);
          await pb.collection("fin_extratos_importados").update(t.lote, {
            total_conciliadas: (lote.total_conciliadas || 0) + 1
          });
        } catch (e) {
          console.warn("Erro ao atualizar contadores do lote:", e.message);
        }
      }
    }

    for (const l of lancamentos) {
      await pb.collection("fin_lancamentos").update(l.id, {
        status: "pago",
        transacao_bancaria: transacoes[0].id,
        valor_conciliado_centavos: l.valor_centavos
      });
    }

    if (lancamentoTaxaId) {
      await pb.collection("fin_lancamentos").update(lancamentoTaxaId, {
        transacao_bancaria: transacoes[0].id,
        valor_conciliado_centavos: -Math.abs(valorTaxaCentavos)
      });
    }

    await logAction(currentUser, "FINANCEIRO", "CONCILIACAO_GRUPO_CRIADO", `Grupo de conciliação N:M criado com sucesso. Taxa: R$ ${valorTaxaCentavos/100}`, {
      grupoId: grupo.id,
      transacoesCount: transacaoIds.length,
      lancamentosCount: lancamentoIds.length,
      valorTaxaCentavos
    });

    return grupo;
  },

  async getRegrasDescritor() {
    try {
      return await pb.collection("fin_regras_descritor").getFullList({
        sort: "-created",
        expand: "categoria_sugerida"
      });
    } catch (err) {
      console.warn("[Conciliacao] Erro ao buscar regras por descritor:", err.message);
      return [];
    }
  },

  async criarRegraDescritor(padrao, categoriaId, tipoSugerido, criadaDe = "aprendida", contaBancariaId = null) {
    try {
      return await pb.collection("fin_regras_descritor").create({
        padrao: padrao.trim(),
        categoria_sugerida: categoriaId,
        tipo_sugerido: tipoSugerido,
        criada_de: criadaDe,
        conta_bancaria: contaBancariaId
      });
    } catch (err) {
      console.error("[Conciliacao] Erro ao criar regra por descritor:", err.message);
      throw err;
    }
  }
};
